import { useQuery } from '@tanstack/react-query';

const BASE_URL = 'https://rossomotori.it/wp-json/v1';

/**
 * Funzione helper per il delay
 */
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Fetch generico per tutte le chiamate API con delay
 */
const fetchData = async (url) => {
  const response = await fetch(`${BASE_URL}${url}`);
  if (!response.ok) {
    throw new Error(`Errore HTTP ${response.status}: ${response.statusText}`);
  }
  const data = await response.json();
  await delay(300);
  return data;
};

/**
 * Hook: Articolo singolo
 */
export const useSingleArticle = (slug) =>
  useQuery({
    queryKey: ['articleDetail', slug],
    queryFn: () => fetchData(`/articles/${slug}`),
    staleTime: 0,
    cacheTime: 0,
    retry: 1,
  });

/**
 * Hook: Articoli tramite tag
 */
export const useArticlesByTag = ({ tag, page = 1, perPage = 10 }) =>
  useQuery({
    queryKey: ['articlesByTag', tag, page],
    queryFn: () => fetchData(`/custom/articles/by-tag?tag=${tag}&page=${page}&per_page=${perPage}`),
    staleTime: 0,
    cacheTime: 0,
  });

/**
 * Hook: Articoli
 */
export const useArticles = ({ page = 1, perPage = 10 }) =>
  useQuery({
    queryKey: ['articles', page, perPage],
    queryFn: () => fetchData(`/articles?page=${page}&per_page=${perPage}`),
    staleTime: 0,
    cacheTime: 0,
  });

/**
 * Hook: Articoli per categoria
 */
export const useCategoryArticles = ({ category, page = 1, perPage = 10 }) =>
  useQuery({
    queryKey: ['categoryArticles', category, page, perPage],
    queryFn: () => fetchData(`/category/${category}?page=${page}&per_page=${perPage}`),
    staleTime: 0,
    cacheTime: 0,
  });

/**
 * Hook: Articoli di un autore
 */
export const useAuthorArticles = ({ author, page = 1, perPage = 10 }) =>
  useQuery({
    queryKey: ['authorArticles', author, page, perPage],
    queryFn: () => fetchData(`/articles/by-author/${author}?page=${page}&per_page=${perPage}`),
    staleTime: 0,
    cacheTime: 0,
  });

/**
 * Hook: Categorie filtrate
 */
export const useCategories = () =>
  useQuery({
    queryKey: ['categories'],
    queryFn: () => fetchData(`/categories`),
    staleTime: 0,
    cacheTime: 0,
  });

/**
 * Hook: Team
 */
export const useTeam = () =>
  useQuery({
    queryKey: ['team'],
    queryFn: () => fetchData(`/team`),
    staleTime: 0,
    cacheTime: 0,
  });

/**
 * Hook: Video da YouTube
 */
export const useYoutubeVideos = () =>
  useQuery({
    queryKey: ['youtubeVideos'],
    queryFn: () => fetchData(`/youtube-videos`),
    staleTime: 14400000, // 4 ore in millisecondi
    cacheTime: 14400000, // 4 ore in millisecondi
    keepPreviousData: true,
  });

  /**
 * Hook: Articolo in modalità preview
 */
export const usePreviewArticle = (id) =>
  useQuery({
    queryKey: ['previewArticle', id],
    queryFn: () => fetchData(`/preview/${id}`),
    staleTime: 0,
    cacheTime: 0,
    retry: 1,
  });
